import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";


import { Button, Icon, PlainButton } from "@culturehq/components";

import { LandingPage, Organization } from "chq";
import { useProfile } from "app/context";
import { useLandingPage } from "app/hooks";
import { getOrganizationSubdomain, Trans } from "app/utils";

import QuickOptions from "components/display/QuickOptions";
import { TimeDiff } from "components/common/Time";
import CopyLink from "components/admin/settings/CopyLink";

interface LandingPageRowProps {
  landingPage: LandingPage;
  onFetch: () => void;
}

const ActionButtons = ({ landingPage, onFetch }: LandingPageRowProps) => {
  const { profile } = useProfile();
  const { handleIsLive } = useLandingPage({ landingPage, onSubmitted: onFetch });
  const { live } = landingPage;

  const [subdomain, setSubdomain] = useState<string>();

  const link = useMemo(() => {
    const getDomain = () => {
      if (process.env.NODE_ENV === "production") return "culturehq.com";
      return "localhost:4000";
    };

    const domain = getDomain();
    const link = `${subdomain}.${domain}${landingPage?.route || "/"}`;

    if (process.env.NODE_ENV === "production") return `https://${link}`;
    return `http://${link}`;
  }, [landingPage.route, subdomain]);

  useEffect(
    () => {
      const orgName = getOrganizationSubdomain(profile.organization as Organization);
      setSubdomain(orgName);
    }, [profile.organization]
  );

  return (
    <QuickOptions>
      <ul className="dropdown-menu lp-menu">
        <li>
          <PlainButton onClick={() => handleIsLive(!live)} className="btn-pause">
            <Icon icon={live ? "pause" : "play-filled"} />
            <Trans>{live ? "Unpublish" : "Publish"}</Trans>
          </PlainButton>
        </li>
        {live && (
          <li>
            <CopyLink
              shareButton
              link={link}
              orgName={profile?.organization?.name}
              ShareButtonRenderer={
                ({ onClick }: any) => (
                  <PlainButton onClick={onClick} className="btn-share">
                    <Icon icon="share-social" />
                    <Trans>Share Page</Trans>
                  </PlainButton>
                )
              }
            />
          </li>
        )}
      </ul>
    </QuickOptions>
  );
};

const getStatusLabel = (live: boolean) => (live
  ? <span className="lp-status lp-status--live">Active</span>
  : <span className="lp-status lp-status--draft">Draft</span>);

const LandingPageRow = ({ landingPage, onFetch }: LandingPageRowProps) => {
  const { creator, name, route, live, createdAt, primary, updatedAt, storiesCount } = landingPage;
  const history = useHistory();

  return (
    <tr>
      <td>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {name}
          {getStatusLabel(live)}
          {primary && <span className="lp-status lp-status--primary">Primary</span>}
        </span>
      </td>
      <td>
        <Trans>{route}</Trans>
      </td>
      <td className="LandingPageRow-createdAt">
        <div className="LandingPageRow-createdAt__creator">{creator?.name || "Unknown"}</div>
        <TimeDiff time={createdAt} />
      </td>
      <td>
        <TimeDiff time={updatedAt} />
      </td>
      <td>
        <Trans>{storiesCount}</Trans>
      </td>
      <td className="Actions-column QuickOptions QuickOptions--no-zindex">
        <div className="LandingPageRow-edit">
          <Button
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              history.push(`/admin/landing-pages/${landingPage.id.toString()}`);
            }}
            icon="edit"
          >
            <Trans>Edit</Trans>
          </Button>
        </div>
        <ActionButtons landingPage={landingPage} onFetch={onFetch} />
      </td>
    </tr>
  );
};

export default LandingPageRow;
